import React, { useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Grid,
    Paper,
    Typography,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import LogsDataService from '../../../services/LogService';

const STABILITY_COLORS = {
    good: '#4caf50',
    warning: '#ff9800',
    bad: '#f44336',
};

const scoreProgressValue = level => {
    if (level === 'good') return 100;
    if (level === 'warning') return 55;
    return 20;
};

const DeviceStabilityCard = ({ device, days, timezone, t }) => {
    const maxBarCount = Math.max(device.maxPerDay, 1);

    return (
        <Paper
            elevation={2}
            sx={{
                p: 1,
                height: '100%',
                borderTop: `3px solid ${STABILITY_COLORS[device.stability]}`,
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.75}
            >
                <Box minWidth={0} flex={1} pr={0.5}>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        title={device.name}
                    >
                        {device.name}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        display="block"
                        title={device.mac}
                    >
                        {device.mac}
                    </Typography>
                </Box>
                <Box position="relative" display="inline-flex" flexShrink={0}>
                    <CircularProgress
                        variant="determinate"
                        value={scoreProgressValue(device.stability)}
                        size={48}
                        thickness={4}
                        sx={{
                            color: STABILITY_COLORS[device.stability],
                        }}
                    />
                    <Box
                        sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            lineHeight={1}
                            sx={{ color: STABILITY_COLORS[device.stability] }}
                        >
                            {device.averagePerDay}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.6rem', lineHeight: 1 }}
                        >
                            {t('systemAdmin.connectionStability.avgPerDay')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    color: STABILITY_COLORS[device.stability],
                    fontWeight: 600,
                    mb: 0.75,
                    display: 'block',
                    lineHeight: 1.2,
                }}
            >
                {t(`systemAdmin.connectionStability.level.${device.stability}`)}
            </Typography>
            <Box display="flex" alignItems="flex-end" gap={0.25} height={72}>
                {device.dailyCounts.map((count, index) => {
                    const level = device.dailyLevels[index];
                    const dayLabel = moment
                        .tz(days[index], timezone)
                        .format('DD.MM');
                    const barHeight = Math.max((count / maxBarCount) * 100, 8);

                    return (
                        <Tooltip
                            key={`${device.mac}-${days[index]}`}
                            title={t(
                                'systemAdmin.connectionStability.barTooltip',
                                { count, date: dayLabel }
                            )}
                        >
                            <Box
                                flex={1}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                height="100%"
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ mb: 0.25, fontSize: '0.65rem' }}
                                >
                                    {count}
                                </Typography>
                                <Box
                                    flexGrow={1}
                                    width="100%"
                                    display="flex"
                                    alignItems="flex-end"
                                >
                                    <Box
                                        width="100%"
                                        height={`${barHeight}%`}
                                        minHeight={6}
                                        borderRadius="2px 2px 0 0"
                                        sx={{
                                            backgroundColor:
                                                STABILITY_COLORS[level],
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 0.25, fontSize: '0.6rem' }}
                                >
                                    {dayLabel}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Paper>
    );
};

const ConnectionStability = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        LogsDataService.getConnectionStability()
            .then(response => {
                setData(response.data);
                setError(null);
            })
            .catch(e => {
                console.log(e);
                setError(
                    t('systemAdmin.connectionStability.loadError')
                );
            })
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: '#003d4d' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" py={4}>
                {error}
            </Typography>
        );
    }

    const legendItems = [
        {
            level: 'good',
            label: t('systemAdmin.connectionStability.legend.good'),
        },
        {
            level: 'warning',
            label: t('systemAdmin.connectionStability.legend.warning'),
        },
        {
            level: 'bad',
            label: t('systemAdmin.connectionStability.legend.bad'),
        },
    ];

    const devices = (data && data.devices) || [];
    const days = (data && data.days) || [];
    const timezone = (data && data.timezone) || 'UTC';

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('systemAdmin.connectionStability.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                {t('systemAdmin.connectionStability.description')}
            </Typography>
            <Paper
                variant="outlined"
                sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}
            >
                {legendItems.map(item => (
                    <Box
                        key={item.level}
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <Box
                            width={16}
                            height={16}
                            borderRadius={1}
                            sx={{
                                backgroundColor: STABILITY_COLORS[item.level],
                            }}
                        />
                        <Typography variant="body2">{item.label}</Typography>
                    </Box>
                ))}
            </Paper>
            {devices.length === 0 ? (
                <Typography align="center" color="text.secondary" py={4}>
                    {t('systemAdmin.connectionStability.noDevices')}
                </Typography>
            ) : (
                <Grid container spacing={1}>
                    {devices.map(device => (
                        <Grid item xs={12} sm={6} md={3} key={device.mac}>
                            <DeviceStabilityCard
                                device={device}
                                days={days}
                                timezone={timezone}
                                t={t}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default ConnectionStability;
